import React, { useEffect, useRef, useState, useContext } from "react";
import PlayerTable from "../../PlayerTable";
import { commitMutation } from "react-relay";
import relayEnvironment from "../../../../../../RelayEnvironment";
import { COMMIT_PLAYER_CHANGES } from "../mutations/CommitPlayerExposureChanges";
import { GreenMachineContext } from "../../GreenMachineContext";
import { GENERATE_LINEUPS } from "../mutations/GenerateLineups";
import LoadingOverlay from "react-loading-overlay";
import "./ExposuresTable.css";
import ExposuresAnalysis from "../ExposuresAnalysis";

export default function ExposuresTable(props) {
  const {
    greenMachineSessionID,
    greenMachineSessionPositions,
    numLineups,
    requestNavigation,
    registerStepNavGuard,
    clearStepNavGuard,
    isNavigating,
    minSalary,
    maxSalary,
    playersPerLineup,
    setErrorMessage,
    numFlexSlots,
    selectedSportName,
    setExistingLineups,
    STAGE_NUMBERS,
  } = useContext(GreenMachineContext);

  const [generating, setGenerating] = useState(false);

  const [unsavedExposureChanges, setUnsavedExposureChanges] = useState({});
  const [sessionExposureChanges, setSessionExposureChanges] = useState({});

  const [totalExposureSalary, setTotalExposureSalary] = useState(
    props.players.reduce((a, b) => +a + +b.exposure * +b.slatePlayer.salary, 0)
  );

  const flexablePlayers = props.players.filter((player) =>
    player.slatePlayer.rosterSlots.edges
      .map((rosterSlot) => rosterSlot.node.name)
      .some((name) => {
        return name === "FLEX" || name === "S-FLEX";
      })
  );

  const flexablePositions = flexablePlayers
    .map((player) => {
      return player.slatePlayer.rosterSlots.edges
        .map((rosterSlot) => rosterSlot.node.name)
        .filter((name) => name !== "FLEX" && name !== "S-FLEX")[0];
    })
    .filter((value, index, self) => self.indexOf(value) === index);

  function updateUnsavedExposures(id, state, salary, change) {
    setUnsavedExposureChanges({ ...unsavedExposureChanges, [id]: state });
    setSessionExposureChanges({ ...sessionExposureChanges, [id]: state });

    const totalSalaryChange = salary * change;

    setTotalExposureSalary(totalExposureSalary + totalSalaryChange);
  }

  // Persist pending exposure edits. Resolves true on success (or no-op).
  function saveExposures() {
    return new Promise((resolve) => {
      setErrorMessage(undefined);

      let exposures = [];
      for (let [pId, exposure] of Object.entries(unsavedExposureChanges)) {
        if (isNaN(exposure)) {
          exposure = 0;
        }
        exposures.push({ gmPlayerId: pId, exposure: exposure });
      }

      if (exposures.length === 0) {
        resolve(true);
        return;
      }

      commitMutation(relayEnvironment, {
        mutation: COMMIT_PLAYER_CHANGES,
        variables: { input: { exposures: exposures } },
        onCompleted: (content, errors) => {
          if (!errors) {
            setUnsavedExposureChanges({});
            resolve(true);
          } else {
            setErrorMessage(errors[0].message);
            resolve(false);
          }
        },
        onError: (error) => {
          console.log(error);
          setErrorMessage("Something went wrong saving your changes.");
          resolve(false);
        },
      });
    });
  }

  // PlayerTable triggers an explicit save through this thin wrapper.
  function commitChanges() {
    saveExposures();
  }

  // Nav guard: always save exposures first. Only run lineup generation when
  // moving FORWARD to the Lineups step — a backward/sideways chevron jump
  // (e.g. to Players or Rules) just saves and navigates.
  const saveRef = useRef();
  saveRef.current = (targetStage) =>
    saveExposures().then((saved) => {
      if (!saved) {
        return false;
      }
      if (targetStage !== STAGE_NUMBERS.viewLineups) {
        return true;
      }

      return new Promise((resolve) => {
        setGenerating(true);
        setErrorMessage(undefined);

        commitMutation(relayEnvironment, {
          mutation: GENERATE_LINEUPS,
          variables: {
            input: { greenMachineSessionId: greenMachineSessionID },
          },
          onCompleted: (content, errors) => {
            setGenerating(false);
            if (!errors) {
              setExistingLineups(true);
              resolve(true);
            } else {
              setErrorMessage(errors[0].message);
              resolve(false);
            }
          },
          onError: (error) => {
            console.log(error);
            setGenerating(false);
            setErrorMessage("Something went wrong generating lineups.");
            resolve(false);
          },
        });
      });
    });

  useEffect(() => {
    registerStepNavGuard({ saveAndValidate: (t) => saveRef.current(t) });
    return () => clearStepNavGuard();
  }, [registerStepNavGuard, clearStepNavGuard]);

  function zeroExposures(e) {
    e.preventDefault();

    const positions = props.players.map((player) => [
      player.id,
      player.slatePlayer.player.name,
      player.slatePlayer.rosterSlots.edges.map((edge) => edge.node.name),
    ]);

    let newExposures = {};

    props.players.map((player) => {
      newExposures[player.id] = 0;
    });

    setSessionExposureChanges(newExposures);
    setUnsavedExposureChanges(newExposures);
    setTotalExposureSalary(0);
  }

  function equalExposures(e) {
    e.preventDefault();

    const positions = props.players.map((player) => [
      player.id,
      player.slatePlayer.player.name,
      player.slatePlayer.rosterSlots.edges.map((edge) => edge.node.name),
      player.slatePlayer.salary,
    ]);

    let newExposures = {};
    let newTotalSalary = 0;

    for (const [position, attrs] of Object.entries(
      greenMachineSessionPositions
    )) {
      const positionMatches = positions.filter((pos) =>
        pos[2].includes(position)
      );
      const numberOfMatches = positionMatches.length;
      const totalForPosition = attrs.amount * numLineups;

      const equalExposure = Math.floor(totalForPosition / numberOfMatches);
      const exposureRemainder = totalForPosition % numberOfMatches;

      const exposureArray = Array(numberOfMatches).fill(equalExposure);

      for (let i = 0; i < exposureRemainder; i++) {
        exposureArray[i] += 1;
      }

      for (let i = 0; i < numberOfMatches; i++) {
        newExposures[positionMatches[i][0]] = exposureArray[i];

        newTotalSalary =
          newTotalSalary + positionMatches[i][3] * exposureArray[i];
      }
    }
    setSessionExposureChanges(newExposures);
    setUnsavedExposureChanges(newExposures);
    setTotalExposureSalary(newTotalSalary);

    if (Object.keys(greenMachineSessionPositions).length > 1) {
      alert(
        "Your exposures have been set. NOTE: your pool has been equalized on a per-position basis. Some differences in exposure between different positions is expected."
      );
    }
  }

  let selectedPlayers = props.players;

  React.useEffect(() => {
    selectedPlayers = selectedPlayers.filter((player) => {
      return !(player.id in sessionExposureChanges);
    });
  }, [sessionExposureChanges]);

  let totalExposure = selectedPlayers.reduce((a, b) => {
    let playerExposure = +b.exposure;

    if (b.id in sessionExposureChanges) {
      const sessionExposureChange = sessionExposureChanges[b.id];

      if (!isNaN(sessionExposureChange)) {
        playerExposure = sessionExposureChange;
      } else {
        playerExposure = 0;
      }
    }

    return +a + playerExposure;
  }, 0);

  const minExposureSalary = minSalary * numLineups;
  const maxExposureSalary = maxSalary * numLineups;

  const minExposureSalaryDisplay = (
    <strong>{Number(minExposureSalary).toLocaleString()}</strong>
  );

  return (
    <div>
      <LoadingOverlay spinner active={generating} text="Generating lineups...">
        <div className="gm-step-actions">
          <button
            type="button"
            className="gm-btn-secondary"
            onClick={equalExposures}
          >
            Equal Exposures
          </button>
          <button
            type="button"
            className="gm-btn-secondary"
            onClick={zeroExposures}
          >
            Reset Exposures
          </button>
          <button
            type="button"
            className="gm-btn-primary"
            disabled={isNavigating}
            onClick={() => requestNavigation(STAGE_NUMBERS.viewLineups)}
          >
            Generate Lineups
            <svg className="gm-cta-arrow" viewBox="0 0 24 24">
              <path d="M8 5l8 7-8 7V5z" />
            </svg>
          </button>
        </div>
        {Object.keys(greenMachineSessionPositions).length === 1 ||
        Object.keys(greenMachineSessionPositions).length === 2 ? (
          <>
            <p
              className={
                totalExposure === numLineups * playersPerLineup
                  ? "gm-good-text"
                  : "gm-bad-text"
              }
            >
              <strong>TOTAL EXPOSURE: </strong> {totalExposure}
              <br />
              {totalExposure !== numLineups * playersPerLineup
                ? `(There are ${
                    numLineups * playersPerLineup
                  } roster slots in ${numLineups} lineups. The Green Machine will still run if the total exposure does not equal ${
                    numLineups * playersPerLineup
                  }, but a larger or smaller number may cause your final exposures to be off.`
                : null}
            </p>
            <p
              className={
                totalExposureSalary >= minExposureSalary &&
                totalExposureSalary <= maxExposureSalary
                  ? "gm-good-text"
                  : "gm-bad-text"
              }
            >
              <strong>TOTAL SALARY * EXPOSURE: </strong>
              {" $"}
              {Number(totalExposureSalary).toLocaleString()}
              <br />
              {totalExposureSalary < minExposureSalary ||
              totalExposureSalary > maxExposureSalary
                ? `Based on your config, this number should be between $${Number(
                    minExposureSalary
                  ).toLocaleString()} and $${Number(
                    maxExposureSalary
                  ).toLocaleString()} for optimal performance. ` +
                  (totalExposureSalary > maxExposureSalary
                    ? `You are over by $${
                        totalExposureSalary - maxExposureSalary
                      }`
                    : `You are under by $${
                        minExposureSalary - totalExposureSalary
                      }`)
                : null}
            </p>
          </>
        ) : (
          <ExposuresAnalysis
            players={selectedPlayers}
            positions={greenMachineSessionPositions}
            sessionExposureChanges={sessionExposureChanges}
            totalExposure={totalExposure}
            totalExposureSalary={totalExposureSalary}
            numLineups={numLineups}
            playersPerLineup={playersPerLineup}
            minExposureSalary={minExposureSalary}
            maxExposureSalary={maxExposureSalary}
            minSalary={minSalary}
            maxSalary={maxSalary}
            flexablePositions={flexablePositions}
            numFlexSlots={numFlexSlots}
            selectedSportName={selectedSportName}
          />
        )}
      </LoadingOverlay>

      <PlayerTable
        players={props.players}
        commitChanges={commitChanges}
        updateChanges={updateUnsavedExposures}
        exposure={true}
        sessionExposureChanges={sessionExposureChanges}
      />
    </div>
  );
}
